/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback } from 'react';
import { closePopupIcon, passwordEyeIcon, warningIcon } from '../assets/superAdminAssets';
import { useAppContext } from '../contexts/AppContext';
import { Constants } from '../constants';
import { changePassword, resetPasswordBySuperAdmin } from '../services/profile.service';
import { renderPasswordHelperText } from '../helpers/index.helper';
import { CookieNames, getCookie } from '../helpers/cookie.helper';

const ChangePasswordModal = ({ changePassPopupShow, setChangePassPopup, userdetails, onClose }) => {
  const { showLoader, hideLoader, showToaster, hideToaster } = useAppContext();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    numeric: false,
    special: false,
  });

  const showPassword = (inputId, eyeId) => {
    const input = document.getElementById(inputId);
    const eyeIcon = document.getElementById(eyeId);
    input.type = input.type === 'password' ? 'text' : 'password';
    eyeIcon.classList.toggle('active_pass');
  };

  const closeModal = () => {
    setPassword('');
    setConfirmPassword('');
    setPasswordsMatch(true);
    setPasswordErrors([]);
    setChangePassPopup(false);
    onClose?.();
  };

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numeric: /\d/.test(password),
      special: /[@$%^&*!#%]/.test(password),
    };

    setPasswordValidations(validations);
    setPasswordErrors(Object.entries(validations).filter(([_, valid]) => !valid).map(([rule]) => rule));
  };

  const handleSave = async () => {
    setSubmitted(true);
    try {
      showLoader();
      if (!password?.length) {
        return;
      }
      if (password !== confirmPassword) {
        setPasswordsMatch(false);
        return;
      }

      if (passwordErrors.length > 0) {
        console.error('Validation errors:', passwordErrors);
        return;
      }

      const params = {
        userId: userdetails?.AdminUserId,
        NewPassword: password,
        RoleName: 'Admin',
        SessionId: getCookie(CookieNames.SESSION_ID),
        IsSetting: false,
        Id: userdetails?.CompanyId
      }

      try {
        const response = await changePassword(params);
        const { status, message } = response;
        if (status === 200) {
          showToaster(message, 'success');
          setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
          closeModal();
        }
      } catch (error) {
        showToaster(error.response?.data?.message || 'An error occurred', 'error');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        console.error('Error:', error);
      } finally {
        hideLoader();
      }
    } catch (error) {
      console.log(error);
    } finally {
      hideLoader();
    }
  };


  const handleResendPasswordLink = async () => {
    try {
      showLoader();
      const response = await resetPasswordBySuperAdmin(userdetails?.AdminEmail, userdetails?.CompanyId);
      const { status, message } = response;
      if (status === 200) {
        showToaster(message, 'success');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        closeModal();
      }
    } catch (error) {
      showToaster(error.response?.data?.message || 'An error occurred', 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      console.error('Error:', error);
    } finally {
      hideLoader();
    }
  };

  const handlePasswordChange = useCallback((e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
    setPasswordsMatch(newPassword === confirmPassword);
  }, []);

  const handleConfirmPasswordChange = useCallback((e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    setPasswordsMatch(newConfirmPassword === password);
  }, [password]);

  return (
    <>
      {changePassPopupShow && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeModal}
          ></div>
          <div
            className="modal show d-block fade"
            id="changePassPopup"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="changePassPopupLabel"
            aria-hidden="false"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5 className="modal-title" id="changePassPopupLabel">Change Password</h5>
                    <button type="button" className="bg-transparent border-none" onClick={closeModal}>
                      <span aria-hidden="true">
                        <img src={closePopupIcon} alt="Close" />
                      </span>
                    </button>
                  </div>
                </div>
                <form>
                  <div className="modal-body">
                    {password.length > 0 && !passwordsMatch && (
                      <div className="password-match-toast d-flex justify-content-center">
                        <img className="mr-2" src={warningIcon} alt="" />
                        Passwords must match
                      </div>
                    )}

                    {passwordErrors.length > 0 && (
                      <div className="password-match-toast d-flex justify-content-center">
                        <img className="mr-2" src={warningIcon} alt="" />
                        Password validations failed
                      </div>
                    )}

                    <div className="form-group input-password">
                      <label htmlFor="password">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        value={password}
                        onChange={handlePasswordChange}
                        placeholder="Enter a password"
                      />
                      <img
                        onClick={() => showPassword('password', 'pass_eye')}
                        id="pass_eye"
                        className="pass_eye"
                        src={passwordEyeIcon}
                        alt="Toggle Password Visibility"
                      />
                      {
                        submitted && !password?.length && (
                          <span className="error-message">Password is required</span>
                        )
                      }
                      {renderPasswordHelperText(passwordValidations)}
                    </div>
                    <div className="form-group input-password">
                      <label htmlFor="confirm-password">Confirm Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirm-password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        placeholder="Confirm your password"
                      />
                      <img
                        onClick={() => showPassword('confirm-password', 'confirm_pass_eye')}
                        id="confirm_pass_eye"
                        className="pass_eye"
                        src={passwordEyeIcon}
                        alt="Toggle Confirm Password Visibility"
                      />
                    </div>
                    <a className="lm-button3" onClick={handleResendPasswordLink}>
                      Send password
                    </a>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="lm-button2" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="button" className="lm-button1" onClick={handleSave}>
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ChangePasswordModal;
